// Admin API client: access token in memory, rotating refresh token in storage, one refresh in flight.
const BASE = '/v1';
const RT_KEY = 'aramis-admin-rt';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public fields?: Record<string, string>) {
    super(message);
  }
}

let access: string | null = null;
let refreshing: Promise<boolean> | null = null;
const listeners = new Set<() => void>();
export const onSessionExpired = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };

export function setTokens(t: { accessToken: string; refreshToken: string }) {
  access = t.accessToken;
  try { localStorage.setItem(RT_KEY, t.refreshToken); } catch { /* private mode */ }
}
export function clearTokens() {
  access = null;
  try { localStorage.removeItem(RT_KEY); } catch { /* ignore */ }
}
export const hasRefreshToken = () => { try { return !!localStorage.getItem(RT_KEY); } catch { return false; } };
export const accessToken = () => access;

async function refresh(): Promise<boolean> {
  refreshing ??= (async () => {
    let rt: string | null = null;
    try { rt = localStorage.getItem(RT_KEY); } catch { /* ignore */ }
    if (!rt) return false;
    const res = await fetch(`${BASE}/admin/auth/refresh`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ refreshToken: rt }) });
    if (!res.ok) { clearTokens(); return false; }
    setTokens(await res.json());
    return true;
  })().finally(() => { refreshing = null; });
  return refreshing;
}
export const ensureSession = async () => (access ? true : refresh());

type Query = Record<string, string | number | boolean | null | undefined>;
export function qs(q?: Query) {
  if (!q) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export async function api<T = any>(path: string, opts: { method?: string; body?: unknown; query?: Query; form?: FormData; retried?: boolean } = {}): Promise<T> {
  if (!access && !opts.retried && hasRefreshToken()) await refresh();
  const res = await fetch(`${BASE}${path}${qs(opts.query)}`, {
    method: opts.method ?? (opts.body || opts.form ? 'POST' : 'GET'),
    headers: { ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}), ...(access ? { authorization: `Bearer ${access}` } : {}) },
    body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
  });
  if (res.status === 401 && !opts.retried && !path.startsWith('/admin/auth/login')) {
    if (await refresh()) return api<T>(path, { ...opts, retried: true });
    listeners.forEach((l) => l());
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body?.error?.code ?? 'error', body?.error?.message ?? 'خطایی رخ داد. دوباره تلاش کنید.', body?.error?.fields);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Downloads a file (exports, backups) with the session token. */
export async function download(path: string, query?: Query) {
  await ensureSession();
  const res = await fetch(`${BASE}${path}${qs(query)}`, { headers: access ? { authorization: `Bearer ${access}` } : {} });
  if (!res.ok) throw new ApiError(res.status, 'download_failed', 'دریافت فایل انجام نشد.');
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'aramis-export';
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export async function uploadMedia(files: File[]): Promise<{ items: Media[] }> {
  const form = new FormData();
  files.forEach((f) => form.append('file', f, f.name));
  return api('/admin/media', { form });
}

export interface Media { id: string; kind: string; url: string; variants: Record<string, string>; width: number | null; height: number | null; lqip: string | null; color: string | null; alt: string | null }
export const thumb = (m: Pick<Media, 'variants' | 'url'> | null | undefined, w = 480) => {
  if (!m) return null;
  const sizes = Object.keys(m.variants).map(Number).sort((a, b) => a - b);
  const pick = sizes.find((s) => s >= w) ?? sizes.at(-1);
  return pick ? m.variants[String(pick)]! : m.url;
};
